import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';

interface DisplayOption {
  id: string;
  label: string;
  icon?: string;
}

interface OptionGridProps {
  options: ReadonlyArray<DisplayOption>;
  /** Selected ids. Single-select callers pass an array of length 0 or 1. */
  selectedIds: readonly string[];
  onToggle: (id: string) => void;
  /** Disable not-yet-selected options (used to enforce a multi-select cap). */
  isDisabled?: (id: string) => boolean;
}

/**
 * A 2-column grid of selectable chips, shared by the single-select steps
 * (archetype, vibe) and the multi-select step (accents). Purely presentational
 * — labels arrive already localized; the parent owns selection logic and any
 * cap. Tokens match the established avatar flow (AvatarCreate / AvatarFilters).
 */
export function OptionGrid({ options, selectedIds, onToggle, isDisabled }: OptionGridProps) {
  const haptics = useHaptics();

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {options.map((o) => {
        const selected = selectedIds.includes(o.id);
        const disabled = !selected && (isDisabled?.(o.id) ?? false);
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => {
              haptics.selection();
              onToggle(o.id);
            }}
            className={cn(
              'flex items-center gap-2 rounded-2xl border px-3.5 py-3 text-left text-sm font-semibold transition-colors active:opacity-80',
              selected
                ? 'border-brand bg-brand text-brand-fg'
                : 'border-border bg-bg-secondary text-fg',
              disabled && 'opacity-40',
            )}
          >
            {o.icon && <span aria-hidden className="text-base leading-none">{o.icon}</span>}
            <span className="min-w-0 flex-1 truncate">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
